<template>
    <div
        v-bind="$attrs"
        class="mockup-svg-container"
        :class="{shadow:isShadowEnabled, safari:isSafari}"
    >
        <InlineSvg ref="inlineSvg" :src="svgSrc" @loaded="svgLoaded" />
    </div>
</template>

<script>
import InlineSvg from "vue-inline-svg";
import { MockupType } from "@/scripts/common/mockupStore/mockupStructure.js";
import { registerMockupScrollHint } from "@/scripts/common/utilities/mockupScrollHint.js";

export default {
    name: "Mockup",
    props: {
        svgSrc: { type: String, required: true },
        imageSrc: { type: String, required: false },
        media: { type: Object },
        altTag: { type: String },
        imageLoaded: { type: Function, default: null },
        setLightBox: { type: Boolean, default: false },
        index: { type: Number },
        shadow: { type: Boolean, default: false },
        mockup: { type: Object },
        isThumbnail: { type: Boolean, default: false },
        showScrollHint: { type: Boolean, default: false },
        scrollHintSectionKey: { type: String, default: null },
        scrollHintPriority: { type: Number }
    },
    components: { InlineSvg },
    data() {
        return {
            imgElement: null,
            foreignObject: null,
            screenArea: null,
            containerDiv: null,
            wrapperDiv: null,
            mockupId: null,
            defs: null,
            fullSvg: null,
            isSafari: false,
            instanceId: `mockup-scroll-hint-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            isScrollable: false,
            unregisterScrollHint: null
        };
    },
    watch: {
        imageSrc(newSrc) {
            if (this.imgElement) {
                if (!newSrc && this.containerDiv) {
                    this.containerDiv.style.background =
                        "linear-gradient(161deg, #8F8F8F 12.93%, #515151 89.74%)";
                }
                if (this.imgElement.tagName && this.imgElement.tagName.toLowerCase() === "image") {
                    // SVG <image> (Safari path) needs href/xlink:href updates.
                    const safeSrc = newSrc || "";
                    this.imgElement.setAttribute("href", safeSrc);
                    this.imgElement.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", safeSrc);
                    this.resizeSafariImage(safeSrc);
                } else {
                    this.imgElement.src = newSrc;
                }
            }
        }
    },
    created() {
        const userAgent = navigator.userAgent;
        const isIOS =
            /iPhone|iPad|iPod/i.test(userAgent) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
        const isAppleWebKit = /AppleWebKit/i.test(userAgent);
        const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);

        // iOS in-app browsers (for example LinkedIn) use WebKit but often omit
        // the Safari token. Keep them off the foreignObject rendering path,
        // which does not reliably scale and clip embedded HTML in those views.
        this.isSafari = (isIOS && isAppleWebKit) || isSafari;
    },
    computed: {
        isShadowEnabled() {
            return (
                this.shadow &&
                this.mockup.mockupType !== MockupType.OUTLINE &&
                this.mockup.mockupType !== MockupType.SEMI_REALISTIC
            );
        }
    },
    methods: {
        svgLoaded(event) {
            this.onSvgLoad(event);
        },
        async onSvgLoad(event) {
            const svg = event;
            if (!svg) return;
            this.fullSvg = svg;

            // Locate the screen area within the SVG
            this.screenArea =
                svg.querySelector('rect[fill="#6D6D6D"]') ||
                svg.querySelector('path[fill="#6D6D6D"]') ||
                svg.querySelector(`#${this.mockupId}`);
            if (!this.screenArea) {
                console.log("No screen area path found: ", this.svgSrc);
                return;
            } else {
                this.mockupId =
                    "unique_" +
                    Date.now() +
                    "_" +
                    Math.random().toString(36).substring(2, 15);
                this.screenArea.setAttribute("id", this.mockupId);
            }

            let darkScheme = this.svgSrc.includes("dark");
            const noDevice = this.svgSrc.includes("minimal");
            const isRealistic = this.mockup.mockupType === MockupType.REALISTIC;
            this.screenArea.setAttribute(
                "fill",
                noDevice ? "transparent" : (darkScheme || isRealistic) ? "black" : "white"
            );

            // Get the bounding box of the screen area.
            const bbox = this.screenArea.getBBox();

            // === SAFARI BRANCH: Use an SVG <image> element with adjusted stretch/crop behavior ===
            if (this.isSafari) {
                if (this.imageSrc) {
                    // Create an SVG <image> element.
                    this.imgElement = document.createElementNS("http://www.w3.org/2000/svg", "image");
                    this.imgElement.classList.add("mockup-screen", "mockup-screen-safari");
                    this.imgElement.setAttribute("href", this.imageSrc);
                    this.imgElement.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", this.imageSrc);
                    // Set x, y and width based on the screen area's bounding box.
                    this.imgElement.setAttribute("x", bbox.x);
                    this.imgElement.setAttribute("y", bbox.y);
                    this.imgElement.setAttribute("width", bbox.width);
                    // Temporarily set height to bbox.height; it will be adjusted after we know the natural dimensions.
                    this.imgElement.setAttribute("height", bbox.height);

                    // Create a clipPath from the screenArea (clone it to preserve differing border radii).
                    const clipId = "clip_" + this.mockupId;
                    let defs = svg.querySelector("defs");
                    if (!defs) {
                        defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
                        svg.insertBefore(defs, svg.firstChild);
                    }
                    const clipPath = document.createElementNS("http://www.w3.org/2000/svg", "clipPath");
                    clipPath.setAttribute("id", clipId);
                    const clipShape = this.screenArea.cloneNode(true);
                    clipShape.removeAttribute("id");
                    clipPath.appendChild(clipShape);
                    defs.appendChild(clipPath);
                    // Apply the clipPath.
                    this.imgElement.setAttribute("clip-path", `url(#${clipId})`);

                    svg.appendChild(this.imgElement);
                    svg.appendChild(this.createSafariScreenOverlay());
                    this.resizeSafariImage(this.imageSrc);
                } else {
                    // Fallback: If no imageSrc, create a fallback gradient background.
                    let defs = svg.querySelector("defs");
                    if (!defs) {
                        defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
                        svg.insertBefore(defs, svg.firstChild);
                    }
                    if (!svg.querySelector("#fallbackGradient")) {
                        const linearGradient = document.createElementNS("http://www.w3.org/2000/svg", "linearGradient");
                        linearGradient.setAttribute("id", "fallbackGradient");
                        linearGradient.setAttribute("gradientTransform", "rotate(161)");
                        const stop1 = document.createElementNS("http://www.w3.org/2000/svg", "stop");
                        stop1.setAttribute("offset", "12.93%");
                        stop1.setAttribute("stop-color", "#8F8F8F");
                        const stop2 = document.createElementNS("http://www.w3.org/2000/svg", "stop");
                        stop2.setAttribute("offset", "89.74%");
                        stop2.setAttribute("stop-color", "#515151");
                        linearGradient.appendChild(stop1);
                        linearGradient.appendChild(stop2);
                        defs.appendChild(linearGradient);
                    }
                    const rectFallback = document.createElementNS("http://www.w3.org/2000/svg", "rect");
                    rectFallback.classList.add("mockup-screen", "mockup-screen-safari");
                    rectFallback.setAttribute("x", bbox.x);
                    rectFallback.setAttribute("y", bbox.y);
                    rectFallback.setAttribute("width", bbox.width);
                    rectFallback.setAttribute("height", bbox.height);
                    rectFallback.setAttribute("fill", "url(#fallbackGradient)");
                    svg.appendChild(rectFallback);
                    svg.appendChild(this.createSafariScreenOverlay());
                }
                svg.style.overflow = "hidden";
                return;
            }

            // === NON-SAFARI BRANCH (unchanged): Use foreignObject with inner container.
            this.foreignObject = document.createElementNS("http://www.w3.org/2000/svg", "foreignObject");
            this.foreignObject.style.position = "relative";
            this.foreignObject.style.pointerEvents = "all";
            this.foreignObject.classList.add("mockup-screen");

            this.wrapperDiv = document.createElement("div");
            this.wrapperDiv.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
            this.wrapperDiv.classList.add("wrapper");
            const borderRadius = this.extractBorderRadius(this.screenArea);
            this.wrapperDiv.style.borderRadius = `${borderRadius}`;
            this.wrapperDiv.style.overflow = "hidden";

            this.containerDiv = document.createElement("div");
            this.containerDiv.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
            this.containerDiv.classList.add(this.isThumbnail ? "container-mockup" : "scrollable-container-mockup");
            this.containerDiv.addEventListener("scroll", this.syncScrollIndicator);
            this.containerDiv.addEventListener("touchstart", this.stopMockupScrollGesturePropagation, { passive: true });
            this.containerDiv.addEventListener("touchmove", this.stopMockupScrollGesturePropagation, { passive: true });
            this.containerDiv.addEventListener("pointerdown", this.stopMockupScrollGesturePropagation);
            this.containerDiv.addEventListener("pointermove", this.stopMockupScrollGesturePropagation);

            if (!this.imageSrc) {
                this.containerDiv.style.background =
                    "linear-gradient(161deg, #8F8F8F 12.93%, #515151 89.74%)";
            }

            this.imgElement = document.createElement("img");
            this.imgElement.src = this.imageSrc;
            this.imgElement.setAttribute("alt", this.altTag || "");

            if (this.media && this.forcedAspectRatio) {
                this.imgElement.classList.add(`aspect-ratio-${this.forcedAspectRatio}`);
            }

            this.imgElement.onload = () => {
                if (this.imageLoaded) {
                    this.imageLoaded(this.imgElement);
                }
                this.syncScrollIndicator();
            };

            this.imgElement.style.width = "100%";
            this.imgElement.style.height = this.isThumbnail ? "100%" : "auto";
            this.imgElement.style.display = "block";
            // A screenshot taller than the device screen is cropped on the card. The top of a
            // screen is the part that identifies it, and it is what the case-study hero shows,
            // so anchor there rather than at the middle of the page.
            this.imgElement.style.objectPosition = this.isThumbnail ? "top center" : "top left";
            this.imgElement.style.objectFit = this.isThumbnail ? "cover" : "initial";
            this.imgElement.style.maxHeight = "unset";

            this.wrapperDiv.appendChild(this.containerDiv);
            this.containerDiv.appendChild(this.imgElement);
            this.foreignObject.appendChild(this.wrapperDiv);
            svg.appendChild(this.foreignObject);

            this.reorderSmallerElements(svg);
            this.updateScreenLayout();
            this.registerScrollHintCandidate();
        },
        createSafariScreenOverlay() {
            const overlay = this.screenArea.cloneNode(true);
            overlay.removeAttribute("id");
            overlay.removeAttribute("stroke");
            overlay.removeAttribute("stroke-width");
            overlay.removeAttribute("filter");
            overlay.classList.add("mockup-screen-overlay");
            overlay.setAttribute("fill", "rgba(0, 0, 0, 0.12)");
            overlay.setAttribute("opacity", "0");
            overlay.setAttribute("pointer-events", "none");
            return overlay;
        },
        resizeSafariImage(src) {
            if (!this.isSafari || !this.imgElement || this.imgElement.tagName.toLowerCase() !== "image" || !src) {
                return;
            }

            const bbox = this.screenArea?.getBBox();
            if (!bbox?.width || !bbox?.height) return;

            const requestSrc = src;
            const tempImg = new Image();
            tempImg.onload = () => {
                // Ignore a response for an image that has already been replaced.
                if (requestSrc !== this.imageSrc || this.imgElement?.getAttribute("href") !== requestSrc) {
                    return;
                }

                const naturalWidth = tempImg.naturalWidth;
                const naturalHeight = tempImg.naturalHeight;
                if (!naturalWidth || !naturalHeight) return;

                if (this.isThumbnail) {
                    this.imgElement.setAttribute("height", bbox.height);
                    this.imgElement.setAttribute("preserveAspectRatio", "xMidYMid slice");
                } else {
                    const scale = bbox.width / naturalWidth;
                    const scaledHeight = naturalHeight * scale;
                    if (scaledHeight > bbox.height) {
                        this.imgElement.setAttribute("height", bbox.height);
                        this.imgElement.setAttribute("preserveAspectRatio", "xMinYMin slice");
                    } else {
                        this.imgElement.setAttribute("height", scaledHeight);
                        this.imgElement.setAttribute("preserveAspectRatio", "xMinYMin meet");
                    }
                }

                this.imageLoaded?.(this.imgElement);
            };
            tempImg.onerror = () => {
                if (requestSrc === this.imageSrc) {
                    console.warn("Unable to load Safari mockup image:", requestSrc);
                }
            };
            tempImg.src = requestSrc;
        },
        extractBorderRadius(element) {
            if (element.tagName === "rect") {
                const rx = parseFloat(element.getAttribute("rx") || "0");
                const ry = parseFloat(element.getAttribute("ry") || rx);
                return `${rx}px ${rx}px ${ry}px ${ry}px`;
            } else if (element.tagName === "path") {
                let isLegacy = this.svgSrc.includes("v1");
                if (this.svgSrc.includes("mobile")) {
                    if (isLegacy && !this.svgSrc.includes("semirealistic")) {
                        return "32px";
                    }
                    if (this.svgSrc.includes("browser")) {
                        return "0px 0px 17px 17px";
                    }
                    return "52px";
                }
                if (this.svgSrc.includes("tablet")) {
                    if (isLegacy) {
                        return "16px";
                    }
                    if (this.svgSrc.includes("browser")) {
                        return "0px 0px 17px 17px";
                    }
                    return "29px";
                }
                if (this.svgSrc.includes("laptop")) {
                    if (this.svgSrc.includes("browser")) {
                        return "0px 0px 17px 17px";
                    }
                }
                if (this.svgSrc.includes("desktop")) {
                    if (this.svgSrc.includes("browser")) {
                        return "0px 0px 17px 17px";
                    }
                }
                return "0px 0px 0px 0px";
            }
            return "0px 0px 0px 0px";
        },
        reorderSmallerElements(svg) {
            if (!this.screenArea) return;
            const screenBBox = this.screenArea.getBBox();
            const smallerElements = [];
            svg.querySelectorAll("rect, path, circle").forEach(element => {
                const bbox = element.getBBox();
                if (
                    bbox.width < screenBBox.width &&
                    bbox.height < screenBBox.height &&
                    (element.tagName === "circle" ||
                        ((bbox.width > 118 && bbox.height < 80) || (bbox.height > 118 && bbox.width < 80))
                    ) &&
                    element !== this.screenArea
                ) {
                    smallerElements.push(element);
                }
            });
            smallerElements.forEach(element => {
                svg.appendChild(element);
            });
        },
        updateScreenLayout() {
            if (!this.foreignObject || !this.screenArea) return;
            const bbox = this.screenArea.getBBox();
            let screenWidth = bbox.width + 4;
            let screenHeight = bbox.height + 4;
            let screenX = bbox.x - 2;
            let screenY = bbox.y - 2;
            this.foreignObject.setAttribute("x", screenX);
            this.foreignObject.setAttribute("y", screenY);
            this.foreignObject.setAttribute("width", screenWidth);
            this.foreignObject.setAttribute("height", screenHeight);
            this.syncScrollIndicator();
        },
        syncScrollIndicator() {
            if (!this.containerDiv || !this.wrapperDiv) return;

            const trackInset = 8;
            const trackHeight = Math.max(this.containerDiv.clientHeight - (trackInset * 2), 0);
            const maxScroll = Math.max(this.containerDiv.scrollHeight - this.containerDiv.clientHeight, 0);
            this.isScrollable = maxScroll > 0;
            const visibleRatio = this.containerDiv.clientHeight / this.containerDiv.scrollHeight;
            const thumbHeight = maxScroll > 0
                ? Math.max(trackHeight * visibleRatio, 24)
                : trackHeight;
            const travel = Math.max(trackHeight - thumbHeight, 0);
            const progress = maxScroll > 0 ? this.containerDiv.scrollTop / maxScroll : 0;
            const thumbTop = trackInset + (travel * progress);

            this.wrapperDiv.style.setProperty("--mockup-scroll-thumb-top", `${thumbTop}px`);
            this.wrapperDiv.style.setProperty("--mockup-scroll-thumb-height", `${thumbHeight}px`);
        },
        stopMockupScrollGesturePropagation(event) {
            if (!this.isScrollable) return;
            event.stopPropagation();
        },
        registerScrollHintCandidate() {
            if (!this.scrollHintSectionKey || !this.containerDiv) return;

            this.unregisterScrollHint?.();
            this.unregisterScrollHint = registerMockupScrollHint(this.scrollHintSectionKey, this.instanceId, {
                isEligible: () => this.showScrollHint && this.isScrollable,
                getPriority: () => this.scrollHintPriority,
                play: () => this.runScrollHintAnimation()
            });
        },
        runScrollHintAnimation() {
            if (!this.showScrollHint || !this.containerDiv) return;

            const easeInOut = (t) => 0.5 * (1 - Math.cos(Math.PI * t));
            const easeOut = (t) => 1 - Math.pow(1 - t, 3);
            const animateScroll = (from, to, duration, easing) => new Promise((resolve) => {
                const start = performance.now();
                const step = (now) => {
                    const progress = Math.min((now - start) / duration, 1);
                    this.containerDiv.scrollTop = from + ((to - from) * easing(progress));
                    if (progress < 1) {
                        requestAnimationFrame(step);
                        return;
                    }
                    resolve();
                };
                requestAnimationFrame(step);
            });

            requestAnimationFrame(async () => {
                await animateScroll(0, 48, 400, easeInOut);
                await new Promise((resolve) => setTimeout(resolve, 100));
                await animateScroll(48, 0, 300, easeOut);
            });
        }
    },
    beforeUnmount() {
        if (this.containerDiv) {
            this.containerDiv.removeEventListener("scroll", this.syncScrollIndicator);
            this.containerDiv.removeEventListener("touchstart", this.stopMockupScrollGesturePropagation);
            this.containerDiv.removeEventListener("touchmove", this.stopMockupScrollGesturePropagation);
            this.containerDiv.removeEventListener("pointerdown", this.stopMockupScrollGesturePropagation);
            this.containerDiv.removeEventListener("pointermove", this.stopMockupScrollGesturePropagation);
        }
        this.unregisterScrollHint?.();
    }
};
</script>

<!-- Styles for scrollbars and container -->
<style lang="scss">
.mockup-screen {
    pointer-events: all;
    overflow: hidden;
    padding: 2px;
}
.mockup-screen-overlay {
    opacity: 0;
    pointer-events: none;
}
.wrapper {
    border-radius: inherit;
    height: 100%;
    position: relative;
}
.container-mockup {
    height: 100%;
    pointer-events: all !important;
}
.mockup.scrollable .scrollable-container-mockup {
    &::-webkit-scrollbar {
        width: 0;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    height: 100%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    pointer-events: all !important;
    touch-action: pan-y;
}

.mockup.scrollable .wrapper::before {
    content: "";
    position: absolute;
    top: 8px;
    right: 3px;
    bottom: 8px;
    width: 5px;
    border-radius: 999px;
    background: var(--asphalt20);
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
    z-index: 2;
}

.mockup.scrollable .wrapper::after {
    content: "";
    position: absolute;
    top: var(--mockup-scroll-thumb-top, 8px);
    right: 3px;
    width: 5px;
    height: var(--mockup-scroll-thumb-height, 32px);
    border-radius: 999px;
    background: var(--asphalt50);
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
    z-index: 3;
}

.mockup.scrollable:hover .wrapper::before,
.mockup.scrollable:hover .wrapper::after {
    opacity: 1;
}
.mockup-svg-container {
    display: inline-block;
    width: 100%;
    max-width: 100%;
    height: 100%;

    &:not(.safari) {
        &.shadow {
            filter: drop-shadow(0px 20px 44px rgba(0, 0, 0, 0.2));
        }
    }
    img {

        border-radius: inherit;
        max-height: unset;
        height: unset;
    }
    svg {
        width: 100%;
        height: 100%;
        max-height: 715px;
        transition: transform 0.3s ease-in-out;
    }
}
</style>
